<template>
    <div class="page">
        <div class="back">
            <div style="display: inline-block;text-align: left;">
                <h1 style="font-size: 1.5rem;">{{t('writingSkill.moreSkills')}}</h1>
            </div>
        </div>
        <div class="list">
            <a style="text-decoration: none;"
               class="list-item"
               v-for="item in skills"
               :key="item.id"
               :href="`/${lang}/writingSkillDetail/${item.documentId}`">
                <div class="item-content">
                    <div class="item-info u-line-3">
                        <h2 style="font-size:1.2rem">{{ item.title }}</h2></div>
                    <div class="item-time">{{ $formatDate(item.createdAt) }}</div>
                </div>
            </a>
        </div>
        <!-- 分页导航 -->
        <div class="pagination">
            <button
                    v-if="pagination.page > 1"
                    @click="changePage(pagination.page - 1)"
            >
                Previous
            </button>
            <button
                    v-if="pagination.page < pagination.pageCount"
                    @click="changePage(pagination.page + 1)"
            >
                Next
            </button>
        </div>
    </div>
</template>

<script setup>

    import back from '../assets/list/back.png'
    import { debounceFunc } from '../utils/util'
    import { getWritingSkills } from "@/api/api";
    import i18n from '../locale/i18n';
    import { useRouter, useRoute } from 'vue-router';
    import { useHead,useSeoMeta } from '@vueuse/head';
    import { formatDate } from '../utils/dateFormatter';
    import { inject } from 'vue';
    import { getInitialLanguage } from '../utils/language';

    const lang = getInitialLanguage();
    const router = useRouter();
    const route = useRoute();
    const t = (key) => i18n.global.t(key);

    const skills = ref([]);

    const pagination = ref({ page: 1, pageCount: 1 });

    // 初始化分页参数
    const currentPage = ref(parseInt(route.params.page) || 1);

    const loadSkills = async (page) => {
      try {
          const { data, meta } = await getWritingSkills(page);
          skills.value = data;
          pagination.value = meta;
      } catch (error) {
          console.error("获取写作技巧列表失败：", error);
      }
    }
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "url": "https://ielts.xzlink.com", // 当前网站 URL
      "name": t("writingSkill.title"), // 网站名称
      "description": t("writingSkill.description"), // 描述
      "publisher": {
        "@type": "Organization",
        "name": t("meta.name"),
        "logo": {
          "@type": "ImageObject",
          "url": "https://ielts.xzlink.com/favicon.ico" // Logo URL
        }
      }
    };
    const jsonLd1 = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": t("writingSkill.title"),
      "operatingSystem": "Web",
      "applicationCategory": "EducationalApplication",
      "url": "https://ielts.xzlink.com",
      "description": "",
      "offers": {
        "@type": "Offer",
        "price": "0.00",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock"
      }
    };
    // 服务端或客户端获取数据
    if (import.meta.env.SSR) {
      const initialState = inject('initialState');
      const data = initialState?.data || {};
      const meta = initialState?.meta || {};
      skills.value = data;
      pagination.value = meta;
      useHead({
        htmlAttrs: {
          lang // 动态设置 lang 属性
        },
        title: t("writingSkill.title"),
        meta: [
          { name: 'description', content: t("writingSkill.description") },
          { name: 'keywords', content: t("writingSkill.keywords") },
        ],
        script: [
          {
            type: "application/ld+json",
            children: JSON.stringify(jsonLd) // 转为字符串插入到 <script> 标签中
          },
          {
            type: "application/ld+json",
            children: JSON.stringify(jsonLd1) // 转为字符串插入到 <script> 标签中
          }
        ]
      });
      useSeoMeta({
        ogTitle: t("writingSkill.title"),
        ogDescription: t("writingSkill.description"),
        ogImage: 'https://ielts.xzlink.com/favicon.ico',
        ogUrl: 'https://ielts.xzlink.com',
        ogType:'website',
      })
    } else {
      onMounted(() => {
        loadSkills(currentPage.value);
      })
      // 监听路由变化，更新数据
      watch(
        () => route.params.page,
        (newPage) => {
          currentPage.value = parseInt(newPage) || 1;
          loadSkills(currentPage.value);
        },
        { immediate: true }
      );
    }

    // 切换分页
    const changePage = (page) => {
      router.push(`/${lang}/writingSkill/page/${page}`);
    };

</script>

<style lang="less" scoped>
    .page {
      width: 100%; /* 页面宽度适应屏幕 */
      height: 100%;
      padding: 5vw; /* 给页面增加 padding，防止内容贴边 */

      .back {
        cursor: pointer;
        display: flex; /* 使用 flexbox 布局 */
        justify-content: space-between; /* 左右两部分分布在两端 */
        align-items: center; /* 垂直居中对齐 */
        margin-bottom: 5vw; /* 标题和内容之间加点间距 */

        img {
          width: 8vw; /* 设置图片宽度，使用 vw 适配 */
          height: auto;
        }
        span {
          font-size: 5vw; /* 动态调整字体大小 */
          color: rgba(0, 0, 0, 1);
        }
      }

      .list {
        display: flex;
        flex-wrap: wrap;
        margin-top: 5vw; /* 给列表添加顶部间距 */

        .list-item {
          display: flex;
          width: 100%;
          height: auto;
          border-radius: 2vw;
          background: rgba(255, 255, 255, 1);
          margin-bottom: 5vw;
          padding: 4vw;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.1); /* 添加阴影让内容更加突出 */

          .item-content {
            display: flex;
            flex-direction: column;
            justify-content: space-between;

            .item-info {
              font-size: 4vw;
              font-weight: 600;
              color: rgba(0, 0, 0, 1);
              margin-bottom: 2vw;
            }

            .item-time {
              font-size: 3.5vw;
              color: rgba(153, 153, 153, 1);
            }
          }
        }
      }

      /* 分页导航按钮样式 */
      .pagination {
        display: flex;
        justify-content: center;
        margin-top: 8vw;
      }

      .pagination button {
        margin-right: 5vw;
        padding: 2.5vw 5vw;
        font-size: 4vw; /* 适应移动端按钮大小 */
        color: white;
        background-color: #007bff; /* 蓝色背景 */
        border: none;
        border-radius: 2vw;
        cursor: pointer;
        transition: background-color 0.3s ease;
      }
    }

    /* 小屏幕适配 */
    @media (max-width: 600px) {
      .page {
        padding: 10vw;
      }

      .back span {
        font-size: 6vw; /* 更大字体适配 */
      }

      .list-item {
        padding: 8vw;
        margin-bottom: 8vw;
      }

      .item-info {
        font-size: 5vw; /* 适配小屏 */
      }

      .item-time {
        font-size: 4.5vw;
      }

      .pagination button {
        font-size: 5vw;
        padding: 3vw 6vw; /* 调整按钮的大小 */
      }
    }

</style>
